<script setup lang="ts">
import { computed, watch, ref, nextTick } from "vue";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useDisplay } from "vuetify";
import {
  useValidatorField,
  type BaseValidatorProps,
} from "@/composables/ui/inputs/useValidatorField";
import { useDateMask } from "@/composables/ui/inputs/useDateMask";
import { useI18n } from "vue-i18n";
import { formatForElementId, toCamelCase, getFixedDayjs } from "@/utils/Formatters";
import type { ESubjects } from "@/api/models/auth/ability";
import { useAbility } from "@casl/vue";
import { useSettingsStore } from "@/stores/settingsStore";
import { humanizeErrorMessage } from "@/utils/Formatters";
import MenuDatePicker from "@/components/ui/inputs/MenuDatePicker.vue";
import { INVALID_DATE_VALUE } from "@/validationSchemas/generic/dateValidationSchema";

dayjs.extend(customParseFormat);

interface Props extends BaseValidatorProps {
  // modelValue?: string | Date | null;
  min?: Date | string;
  max?: Date | string;
  subject?: ESubjects;
  inputFormat?: string;
  filledDisplayFormat?: string;
  datetime?: boolean;
}

const { t } = useI18n();
const { can } = useAbility();

const props = withDefaults(defineProps<Props>(), {
  variant: "outlined",
  density: "comfortable",
  hideDetails: false,
  disabled: false,
  readonly: false,
  required: false,
  transparent: false,
  datetime: false,
  waitingForBackend: false,
  showValidationIcon: true,
  touched: false,
  min: dayjs().subtract(150, "year").toISOString(),
  max: dayjs().add(150, "year").toISOString(),
  withWarning: null,
});

const emit = defineEmits<{
  (e: "changed", payload: { value: string | null; dayjs: Dayjs | null }): void;
}>();

const inputText = ref<string>("");
const isFocused = ref(false);
const menuOpen = ref(false);

const {
  value,
  errorMessage,
  validationIcon,
  validationColor,
  baseFieldProps,
  setErrors,
  validate,
  setTouched,
} = useValidatorField(props, isFocused);

const displayMessage = computed(() => {
  if (errorMessage.value) return errorMessage.value;

  if (props.withWarning?.test && props.withWarning?.message) {
    return props.withWarning.message;
  }

  return undefined;
});

const hasWarningOnly = computed(() => {
  return !errorMessage.value && props.withWarning?.test && props.withWarning?.message;
});

const isErrorWhileFocused = computed(() => {
  return !!errorMessage.value && isFocused.value;
});

const displayColor = computed(() => {
  return hasWarningOnly.value || isErrorWhileFocused.value ? "warning" : validationColor.value;
});

const { smAndDown } = useDisplay();
const { dateFormatInput, dateFormatDisplay } = useSettingsStore();

const defaults = computed(() => ({
  DATE: {
    INPUT: dateFormatInput,
    FILLED_DISPLAY: dateFormatDisplay,
    VALUE: "YYYY-MM-DD",
  },
  DATETIME: {
    INPUT: `${dateFormatInput} HH:mm`,
    FILLED_DISPLAY: `${dateFormatDisplay} HH:mm`,
    VALUE: "YYYY-MM-DDTHH:mm:ssZ",
  },
}));

const current = computed(() => (props.datetime ? defaults.value.DATETIME : defaults.value.DATE));
const inputFormat = computed(() => props.inputFormat || current.value.INPUT);
const filledDisplayFormat = computed(
  () => props.filledDisplayFormat || current.value.FILLED_DISPLAY
);

const pickerModel = computed({
  get: () => {
    if (!value.value || value.value === INVALID_DATE_VALUE) return null;
    const parsed = getFixedDayjs(value.value as any);
    return parsed.isValid() ? parsed.toDate() : null;
  },
  set: (val: Date | string | null) => {
    if (!val) {
      // Only clear if it's not already null to avoid loops if needed,
      // but generally safe to just set.
      if (value.value) {
        value.value = null;
        emit("changed", { value: null, dayjs: null });
        clearFieldError();
      }
      return;
    }
    const d = dayjs(val);
    if (d.isValid()) {
      applyPickedDayjs(d);
    }
  },
});

const { maskedLength, applyDynamicMask, handleKeydown } = useDateMask(inputFormat);

// Boundaries
function asDayjsBoundary(d?: Date | string | null) {
  if (!d) return null;
  const parsed = getFixedDayjs(d);
  return parsed.isValid() ? parsed : null;
}

const minBoundary = computed(() => asDayjsBoundary(props.min));
const maxBoundary = computed(() => asDayjsBoundary(props.max));

function clearFieldError() {
  setErrors([]);
}

function setInvalid(msg: string) {
  setErrors(msg);
}

function applyPickedDayjs(d: Dayjs) {
  const formatted = d.format(current.value.VALUE);

  if (value.value === formatted) {
    if (!isFocused.value) {
      inputText.value = d.format(filledDisplayFormat.value);
    }
    clearFieldError();
    return;
  }

  value.value = formatted;
  setTouched(true);
  clearFieldError();
  emit("changed", { value: formatted, dayjs: d });
  if (!isFocused.value) {
    inputText.value = d.format(filledDisplayFormat.value);
  }
}

function formatBoundary(d: Dayjs) {
  return d.format(filledDisplayFormat.value);
}

function validateParsedDate(parsed: Dayjs): boolean {
  if (!parsed.isValid()) {
    setInvalid(t("generic.errors.invalidDate"));
    return false;
  }

  const candidate = props.datetime ? parsed : parsed.startOf("day");
  const min = minBoundary.value
    ? props.datetime
      ? minBoundary.value
      : minBoundary.value.startOf("day")
    : null;
  const max = maxBoundary.value
    ? props.datetime
      ? maxBoundary.value
      : maxBoundary.value.endOf("day")
    : null;

  if (min && candidate.isBefore(min)) {
    setInvalid(t("generic.errors.dateMustBeAfter", { date: formatBoundary(minBoundary.value!) }));
    return false;
  }

  if (max && candidate.isAfter(max)) {
    setInvalid(t("generic.errors.dateMustBeBefore", { date: formatBoundary(maxBoundary.value!) }));
    return false;
  }

  return true;
}

function tryParseAndEmit(display: string) {
  // No error on partial input
  if (!display || display.length < maskedLength.value) {
    clearFieldError();
    if (!display) {
      value.value = null;
      emit("changed", { value: null, dayjs: null });
    }
    return;
  }

  const parsed = getFixedDayjs(display, inputFormat.value, true);
  if (!validateParsedDate(parsed)) {
    // Park the rejected entry in the field's own value rather than leaving it empty. A later
    // form-level schema run reads these values, and an empty one made `required` the failing
    // rule — overwriting the specific message above with "… is a required field". An
    // out-of-range date keeps its real value so the schema's bound test can name it; an
    // unparseable one gets a sentinel the schema recognises as "invalid", not "missing".
    rememberRejectedValue(
      parsed.isValid() ? parsed.format(current.value.VALUE) : INVALID_DATE_VALUE
    );
    return;
  }

  // Valid
  clearFieldError();
  applyPickedDayjs(parsed);
}

/**
 * Writes the rejected value without disturbing the message `validateParsedDate` just set:
 * assigning `value` triggers vee-validate's own validation, so the field error is re-applied
 * on the next tick if the schema had nothing more specific to say.
 */
function rememberRejectedValue(rejected: string) {
  if (value.value === rejected) {
    return;
  }
  const message = errorMessage.value;
  value.value = rejected;
  setTouched(true);
  nextTick(() => {
    if (message) {
      setErrors(message);
    }
  });
}

watch(
  () => value.value,
  (newVal) => {
    if (!isFocused.value) {
      // The sentinel is bookkeeping, not something to render — keep whatever the user typed
      // on screen so they can correct it, and let the schema own the message.
      if (newVal === INVALID_DATE_VALUE) {
        return;
      }

      const parsed = getFixedDayjs(newVal as any);
      inputText.value = newVal
        ? parsed.isValid()
          ? parsed.format(filledDisplayFormat.value)
          : String(newVal)
        : "";

      if (newVal) {
        validate().then(() => {
          validateParsedDate(parsed);
        });
      } else {
        clearFieldError();
      }
    }
  },
  { immediate: true }
);

const dateFieldProps = computed(() => ({
  ...baseFieldProps.value,
  hideDetails: smAndDown.value ? ("auto" as const) : props.hideDetails && !displayMessage.value,
  label: props.label ?? t(`generic.fields.${toCamelCase(props.fieldName)}`),
  error: !!errorMessage.value && !isErrorWhileFocused.value,
  errorMessages:
    errorMessage.value && !isErrorWhileFocused.value
      ? humanizeErrorMessage(
          t,
          errorMessage.value,
          props.fieldName,
          props.label ?? t(`generic.fields.${props.fieldName}`)
        )
      : undefined,
  messages:
    hasWarningOnly.value || isErrorWhileFocused.value
      ? typeof displayMessage.value === "string"
        ? humanizeErrorMessage(
            t,
            displayMessage.value,
            props.fieldName,
            props.label ?? t(`generic.fields.${props.fieldName}`)
          )
        : displayMessage.value
      : undefined,
  color: displayColor.value,
  persistentPlaceholder: true,
  prependIcon: "",
  autocomplete: "off",
  placeholder: inputFormat.value,
  maxlength: maskedLength.value,
  type: "text",
  inputmode: "numeric",
}));

const elementId = computed(() => formatForElementId(dateFieldProps.value.label));

const isFieldHidden = computed(() => {
  return props.subject && props.fieldName && !can($permActions.READ, props.subject);
});

const permissionDisabled = computed(() => {
  return Boolean(
    props.disabled ||
    (props.subject &&
      props.fieldName &&
      !can($permActions.CREATE, props.subject) &&
      !can($permActions.UPDATE, props.subject))
  );
});

function onKeydown(e: KeyboardEvent) {
  const input = e.target as HTMLInputElement;
  handleKeydown(e, input, (masked) => {
    inputText.value = masked;
    tryParseAndEmit(masked);
  });
}

function onInput(e: Event) {
  const target = e.target as HTMLInputElement;
  const masked = applyDynamicMask(target.value);
  if (masked !== target.value) target.value = masked;
  inputText.value = masked;
  tryParseAndEmit(masked);
}

function onFocus() {
  isFocused.value = true;
  if (value.value && value.value !== INVALID_DATE_VALUE) {
    const parsed = getFixedDayjs(value.value as any);
    if (parsed.isValid()) inputText.value = parsed.format(inputFormat.value);
  }
}

function onBlur() {
  isFocused.value = false;
  if (!inputText.value) {
    value.value = null;
    clearFieldError();
    emit("changed", { value: null, dayjs: null });
    return;
  }
  tryParseAndEmit(inputText.value);
}

// On mobile, the OS soft keyboard can cover the picker menu (which is anchored
// to the calendar icon). Blurring the focused input is the reliable
// cross-platform way to dismiss the keyboard, freeing the space the menu opens
// into. The menu's anchor doesn't move, so it simply becomes visible.
watch(menuOpen, (open) => {
  if (open && smAndDown.value) {
    (document.activeElement as HTMLElement | null)?.blur?.();
  }
});
</script>

<template>
  <v-text-field
    v-if="!isFieldHidden"
    v-model="inputText"
    v-bind="dateFieldProps"
    :class="{ 'date-picker-open': menuOpen }"
    :disabled="permissionDisabled || props.readonly"
    :readonly="smAndDown"
    @focus="onFocus"
    @keydown="onKeydown"
    @input="onInput"
    @blur="onBlur"
    @keydown.enter.prevent
    @click:control="smAndDown && (menuOpen = true)"
    data-testid="date-field-validator"
    :data-test-elementid="elementId"
    :aria-label="dateFieldProps.label"
  >
    <template
      v-if="props.transparent"
      #prepend-inner
    >
      <menu-date-picker
        v-model:selectedDate="pickerModel"
        v-model:menu="menuOpen"
        :min="props.min"
        :max="props.max"
      >
        <template #activator="{ menuProps }">
          <v-avatar
            v-bind="menuProps"
            color="secondary"
            class="tw:!rounded-sm tw:!mr-2 tw:!-mt-1"
            size="x-small"
            role="button"
            tabindex="0"
            @click.stop
            @mousedown.stop.prevent
          >
            <v-icon
              icon="mdi-calendar-month-outline"
              size="small"
            />
          </v-avatar>
        </template>
      </menu-date-picker>
    </template>

    <template
      v-if="!props.transparent"
      #append-inner
    >
      <div class="tw:flex tw:items-center tw:gap-2">
        <menu-date-picker
          v-model:selectedDate="pickerModel"
          v-model:menu="menuOpen"
          :min="props.min"
          :max="props.max"
        >
          <template #activator="{ menuProps }">
            <v-btn
              v-bind="menuProps"
              icon
              variant="text"
              density="comfortable"
              :disabled="permissionDisabled || props.readonly"
              aria-label="date-picker-activator"
              @click.stop
              @mousedown.stop.prevent
            >
              <v-icon
                icon="mdi-calendar-month-outline"
                class="tw:text-on-surface/35!"
              />
            </v-btn>
          </template>
        </menu-date-picker>

        <v-icon
          v-if="props.showValidationIcon"
          :icon="validationIcon"
          :color="displayColor"
          class="tw:transition-all tw:duration-300 tw:ease-out tw:opacity-100"
        />
      </div>
    </template>
  </v-text-field>
</template>

<style src="@/assets/styles/components/fields.scss" scoped lang="scss" />

<style scoped lang="scss">
.v-input {
  font-weight: var(--tw-font-medium);

  :deep(.v-field__input input) {
    font-size: var(--tw-text-ms) !important;
  }

  // Safari paints the native text caret in its own layer that ignores
  // z-index, so a focused input's caret bleeds through the open picker menu.
  // Hide the caret while the menu is open (focus/behavior is unaffected).
  &.date-picker-open :deep(input) {
    caret-color: transparent;
  }

  &.v-input--transparent {
    min-width: 17rem;

    :deep(.v-field) {
      padding-right: 0;
    }
  }

  &.v-input--readonly {
    :deep(.v-input__control) {
      .v-field--variant-outlined .v-field__outline {
        --v-field-border-opacity: 0.38 !important;
      }

      .v-field__input,
      .v-field__input input {
        cursor: pointer !important;
      }
    }
  }
}

.v-field__append-inner > .v-icon {
  opacity: 1 !important;
}
</style>
